import { useCallback, useMemo, useRef } from "react";
import { HotTable } from "@handsontable/react";
import Handsontable from "handsontable";
import { registerAllModules } from "handsontable/registry";
import { registerLanguageDictionary, ptBR } from "handsontable/i18n";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/dist/handsontable.full.min.css";
import { alpha, Box, useTheme } from "@mui/material";
import LoadingPage from "../../../../../componentes/loadingPage";
import { customStyles } from "../../../../../componentes/newEasyAdd/table/tableStyle";
import { FlowTableColumn, FlowTableRow } from "./types";
import { flowTableStyles } from "./tableStyle";

registerLanguageDictionary(ptBR);
registerAllModules();

const EDIT_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>`;
const DELETE_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"/></svg>`;

const ACTIONS_PROP = "actions";
const SELECT_PROP = "selected";

const TEST_ICON_SVG: Record<string, string> = {
  ping: `<path d="M12 14.67 3.41 6.09 2 7.5l8.5 8.5H4v2h16v-2h-6.5l5.15-5.15c.26.1.55.15.85.15 1.38 0 2.5-1.12 2.5-2.5S20.88 6 19.5 6 17 7.12 17 8.5c0 .35.07.67.2.97z"/>`,
  snmp: `<path d="m20.2 5.9.8-.8C19.6 3.7 17.8 3 16 3s-3.6.7-5 2.1l.8.8C13 4.8 14.5 4.2 16 4.2s3 .6 4.2 1.7m-.9.8c-.9-.9-2.1-1.4-3.3-1.4s-2.4.5-3.3 1.4l.8.8c.7-.7 1.6-1 2.5-1s1.8.3 2.5 1zM19 13h-2V9h-2v4H5c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2M8 18H6v-2h2zm3.5 0h-2v-2h2zm3.5 0h-2v-2h2z"/>`,
  pacotes: `<path d="M9.01 14H2v2h7.01v3L13 15l-3.99-4zm5.98-1v-3H22V8h-7.01V5L11 9z"/>`,
};

export function sourceRow<T = any>(
  instance: any,
  visualRow: number,
): T | undefined {
  const physicalRow = instance?.toPhysicalRow?.(visualRow) ?? visualRow;
  const data = instance?.getSettings?.()?.data;
  return Array.isArray(data)
    ? data[physicalRow]
    : instance?.getSourceDataAtRow?.(physicalRow);
}

export function statusIconsRenderer(
  instance: any,
  td: HTMLTableCellElement,
  row: number,
  _col: number,
  _prop: string | number,
  value: any,
) {
  while (td.firstChild) td.removeChild(td.firstChild);

  td.classList.add("chip-cell");

  if (sourceRow<any>(instance, row)?._loading) {
    const loader = document.createElement("div");
    loader.className = "ht-loader-spinner";
    td.appendChild(loader);
    return;
  }

  const container = document.createElement("div");
  container.className = "ht-status-container";

  Object.entries(value ?? {}).forEach(([type, result]) => {
    const icon = document.createElement("span");
    icon.className = `ht-status-icon ${
      result === true
        ? "ht-status-success"
        : result === false
          ? "ht-status-error"
          : "ht-status-null"
    }`;
    icon.title = type.toUpperCase();
    icon.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor">${
      TEST_ICON_SVG[type] ?? TEST_ICON_SVG.ping
    }</svg>`;
    container.appendChild(icon);
  });

  td.appendChild(container);
}

export function errorCellRenderer(
  this: any,
  instance: any,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: any,
  cellProperties: any,
) {
  const isList =
    cellProperties.type === "dropdown" ||
    cellProperties.type === "autocomplete";
  const baseRenderer =
    cellProperties.type === "dropdown"
      ? (Handsontable.renderers as any).DropdownRenderer
      : cellProperties.type === "autocomplete"
        ? (Handsontable.renderers as any).AutocompleteRenderer
        : (Handsontable.renderers as any).TextRenderer;

  baseRenderer.apply(this, [
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  ]);

  td.querySelector(".cell-error-icon")?.remove();
  td.classList.remove("cell-error");
  td.title = "";

  const errorMessage = sourceRow<any>(instance, row)?._errors?.[prop];
  if (!errorMessage) return;

  td.classList.add("cell-error");
  td.title = errorMessage;

  td.style.position = "relative";
  const errorIcon = document.createElement("span");
  errorIcon.className = `cell-error-icon${isList ? " on-list" : ""}`;
  errorIcon.textContent = "!";
  td.appendChild(errorIcon);
}

export interface FlowTableProps<T extends FlowTableRow> {
  id: string;
  rows: T[];
  columns: FlowTableColumn[];
  isEditable?: boolean;
  loadingTable?: boolean;
  onEditRow?: (row: T) => void;
  onDeleteRow?: (row: T) => void;
  onCellChange?: (row: T, prop: string, value: any) => void;
  selectable?: boolean;
  onSelectionChange?: () => void;
  rowClassName?: (row: T) => string | undefined;
  height?: string;
  minHeight?: string;
}

export default function FlowTable<T extends FlowTableRow>(
  props: FlowTableProps<T>,
) {
  const {
    id,
    rows,
    columns: columnsConfig,
    isEditable = false,
    loadingTable = false,
    onEditRow,
    onDeleteRow,
    onCellChange,
    selectable = false,
    onSelectionChange,
    rowClassName,
    height = "55vh",
    minHeight = "450px",
  } = props;

  const theme = useTheme();
  const hotRef = useRef<any>(null);

  const showActions = Boolean(onEditRow || onDeleteRow);
  const isAllSelected =
    rows.length > 0 && rows.every((row) => row.selected === true);

  const actionsRenderer = useCallback(
    (
      _instance: any,
      td: HTMLTableCellElement,
      _row: number,
      _col: number,
      _prop: string | number,
      _value: any,
    ) => {
      while (td.firstChild) {
        td.removeChild(td.firstChild);
      }

      const container = document.createElement("div");
      container.className = "action-cell-container";

      if (onEditRow) {
        const editBtn = document.createElement("div");
        editBtn.className = "action-btn edit";
        editBtn.innerHTML = EDIT_ICON_SVG;
        editBtn.dataset.action = "edit";
        editBtn.title = "Editar";
        container.appendChild(editBtn);
      }

      if (onDeleteRow) {
        const deleteBtn = document.createElement("div");
        deleteBtn.className = "action-btn delete";
        deleteBtn.innerHTML = DELETE_ICON_SVG;
        deleteBtn.dataset.action = "delete";
        deleteBtn.title = "Excluir";
        container.appendChild(deleteBtn);
      }

      td.appendChild(container);
      td.style.textAlign = "center";
      td.style.verticalAlign = "middle";
    },
    [onEditRow, onDeleteRow],
  );

  const columns = useMemo(() => {
    const cols: any[] = columnsConfig.map((column) => ({
      data: column.data,
      type: column.type ?? "text",
      ...(column.source ? { source: column.source } : {}),
      ...(column.width ? { width: column.width } : {}),
      ...(column.readOnly ? { readOnly: true } : {}),
      ...(column.className ? { className: column.className } : {}),
      ...(column.strict !== undefined ? { strict: column.strict } : {}),
      ...(column.allowInvalid !== undefined
        ? { allowInvalid: column.allowInvalid }
        : {}),
      ...(column.renderer ? { renderer: column.renderer } : {}),
    }));

    if (selectable) {
      cols.unshift({ data: SELECT_PROP, type: "checkbox", width: 34 });
    }

    if (showActions) {
      cols.push({
        data: ACTIONS_PROP,
        type: "text",
        readOnly: true,
        focusable: false,
        width: 80,
        className: "htCenter",
      });
    }

    return cols;
  }, [columnsConfig, selectable, showActions]);

  const columnHeaders = useMemo(() => {
    const headers = columnsConfig.map((column) => column.header);

    if (selectable) {
      headers.unshift(
        `<input type="checkbox" class="ht-select-all" ${
          isAllSelected ? "checked" : ""
        } />`,
      );
    }
    if (showActions) headers.push("Ações");

    return headers;
  }, [columnsConfig, isAllSelected, selectable, showActions]);

  const readOnlyProps = useMemo(
    () =>
      new Set(
        columnsConfig
          .filter((column) => column.readOnly)
          .map((column) => column.data),
      ),
    [columnsConfig],
  );

  const cellsConfig = useCallback(
    (row: number, _col: number, prop?: string | number): any => {
      const cellProperties: any = {};

      if (prop === ACTIONS_PROP) {
        cellProperties.readOnly = true;
        cellProperties.renderer = actionsRenderer;
        return cellProperties;
      }

      const className = rowClassName?.(rows[row]);
      if (className) cellProperties.className = className;

      cellProperties.readOnly =
        prop === SELECT_PROP
          ? false
          : !isEditable || readOnlyProps.has(String(prop ?? ""));

      return cellProperties;
    },
    [actionsRenderer, isEditable, readOnlyProps, rowClassName, rows],
  );

  const getColHeaders = useCallback(
    (col: number) => columnHeaders[col],
    [columnHeaders],
  );

  const toggleSelectAll = useCallback(() => {
    const nextState = !rows.every((row) => row.selected === true);
    rows.forEach((row: FlowTableRow) => {
      row.selected = nextState;
    });
    onSelectionChange?.();
  }, [onSelectionChange, rows]);

  const handleBeforeOnCellMouseDown = useCallback(
    (event: any, coords: any) => {
      const hotInstance = hotRef.current?.hotInstance;
      const target = event?.target;
      if (!hotInstance || !target || coords.row < 0) return;

      const prop = hotInstance.colToProp(coords.col);
      if (prop !== ACTIONS_PROP) return;

      const actionBtn = target.closest(".action-btn");
      if (!actionBtn) return;

      const rowData = sourceRow<T>(hotInstance, coords.row);
      if (!rowData) return;

      if (actionBtn.dataset.action === "edit") onEditRow?.(rowData);
      else if (actionBtn.dataset.action === "delete") onDeleteRow?.(rowData);

      event.preventDefault();
    },
    [onEditRow, onDeleteRow],
  );

  const handleAfterOnCellMouseDown = useCallback(
    (event: any, coords: any) => {
      if (!selectable || coords.row !== -1 || coords.col !== 0) return;
      if (event?.target?.closest?.("input.ht-select-all")) return;

      toggleSelectAll();
    },
    [selectable, toggleSelectAll],
  );

  const handleAfterChange = useCallback(
    (changes: any, source: string) => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!changes || !hotInstance || source === "loadData") return;

      let selectionChanged = false;

      changes.forEach(([row, prop, oldValue, newValue]: any) => {
        if (oldValue === newValue) return;

        const rowData = sourceRow<T>(hotInstance, row);
        if (!rowData) return;

        if (prop === SELECT_PROP) selectionChanged = true;
        else onCellChange?.(rowData, String(prop), newValue);
      });

      if (selectionChanged) onSelectionChange?.();
      hotInstance.render();
    },
    [onCellChange, onSelectionChange],
  );

  const handleAfterGetColHeader = useCallback(
    (col: number, TH: HTMLTableCellElement) => {
      const nativeBtn = TH.querySelector(".changeType");
      if (nativeBtn) nativeBtn.parentElement?.removeChild(nativeBtn);

      if (!selectable || col !== 0) return;
      const input = TH.querySelector<HTMLInputElement>("input.ht-select-all");
      if (!input) return;

      input.checked = isAllSelected;
      input.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleSelectAll();
      };
    },
    [isAllSelected, selectable, toggleSelectAll],
  );

  return (
    <>
      <style>{customStyles(theme) + flowTableStyles(theme)}</style>
      <Box
        className={`custom-table-wrapper ${!isEditable ? "view-mode" : ""} ${
          selectable ? "select-mode" : ""
        }`}
        sx={{
          width: "100%",
          minHeight,
          overflow: "auto",
          position: "relative",
        }}
      >
        {loadingTable ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight,
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
              borderRadius: "16px",
            }}
          >
            <LoadingPage mt={"5%"} />
          </Box>
        ) : (
          <HotTable
            id={id}
            ref={hotRef}
            data={rows}
            licenseKey="non-commercial-and-evaluation"
            rowHeaders={false}
            height={height}
            stretchH="all"
            rowHeights={10}
            cells={cellsConfig}
            filters={false}
            colHeaders={getColHeaders}
            columns={columns}
            afterChange={handleAfterChange}
            afterGetColHeader={handleAfterGetColHeader}
            afterOnCellMouseDown={handleAfterOnCellMouseDown}
            beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
            manualColumnResize={true}
            language={ptBR.languageCode}
          />
        )}
      </Box>
    </>
  );
}
